// ScheduledTriggersPanel — UI för P-105: cron/interval-baserade
// scheduled triggers.
//
// Flow:
// 1. User klickar "Schemalägg" → dialog med kind-toggle (interval|cron),
//    interval-slider eller cron-input + live-validation, timezone.
// 2. Submit → createScheduledTrigger() → panel uppdaterar lista.
// 3. Lista visar: label, schedule-description, next_run_at, last_run_at,
//    trigger_count + disable-knapp.
//
// Preview: för interval shows "Var N sekunder"; för cron visar vi den
// beräknade nästa fire-tiden via computeNextRunAt (P-105 runtime).

import { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Calendar, Plus, Trash2, RefreshCw } from 'lucide-react';
import {
  createScheduledTrigger,
  listMyScheduledTriggers,
  disableScheduledTrigger,
  type ScheduledTriggerMetadata,
  type ScheduleKind,
} from '@/lib/scheduledTriggerClient';
import { validateCronExpression } from '@/lib/cronValidation';
import { computeNextRunAt } from '@/lib/scheduledTriggerRuntime';
import { usePlan } from '@/hooks/usePlan';
import { toast } from '@/hooks/use-toast';

interface Props {
  workflowId: string;
}

const INTERVAL_PRESETS: Array<{ label: string; seconds: number }> = [
  { label: 'Var 5:e minut', seconds: 300 },
  { label: 'Var 15:e minut', seconds: 900 },
  { label: 'Var timme', seconds: 3600 },
  { label: 'Var 6:e timme', seconds: 21600 },
  { label: 'Var dag (24h)', seconds: 86400 },
];

const CRON_PRESETS: Array<{ label: string; expression: string }> = [
  { label: 'Varje minut', expression: '0 * * * * *' },
  { label: 'Varje timme, minut 0', expression: '0 0 * * * *' },
  { label: 'Dagligen kl 09:00 UTC', expression: '0 0 9 * * *' },
  { label: 'Vardagar kl 09:00 UTC', expression: '0 0 9 * * 1-5' },
  { label: 'Första dagen i månaden', expression: '0 0 0 1 * *' },
];

const TIMEZONES = [
  'UTC', 'Europe/Stockholm', 'Europe/London', 'America/New_York',
  'America/Los_Angeles', 'Asia/Tokyo',
];

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' });
}

function formatSchedule(trigger: ScheduledTriggerMetadata): string {
  if (trigger.schedule_kind === 'interval') {
    const s = trigger.interval_seconds ?? 0;
    if (s < 60) return `Var ${s}s`;
    if (s < 3600) return `Var ${Math.round(s / 60)}:e min`;
    if (s < 86400) return `Var ${Math.round(s / 3600)}:e timme`;
    return `Var ${Math.round(s / 86400)}:e dag`;
  }
  return `Cron: ${trigger.cron_expression ?? '—'} (${trigger.timezone})`;
}

export function ScheduledTriggersPanel({ workflowId }: Props) {
  const { limits } = usePlan();
  const [triggers, setTriggers] = useState<ScheduledTriggerMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    const result = await listMyScheduledTriggers(workflowId);
    if (result.success) setTriggers(result.triggers);
    setIsLoading(false);
  }, [workflowId]);

  useEffect(() => { void load(); }, [load]);

  const handleDisable = async (id: string, label: string) => {
    const result = await disableScheduledTrigger(id);
    if (result.success) {
      toast({ title: 'Schedule disabled', description: label });
      void load();
    } else {
      toast({ title: 'Kunde inte disable', description: result.error, variant: 'destructive' });
    }
  };

  if (!limits.canRunProcesses) {
    return (
      <div className="p-6 text-center text-xs text-muted-foreground">
        <Calendar className="w-8 h-8 mx-auto mb-2 opacity-40" />
        Schemaläggning kräver Enterprise-plan.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="scheduled-triggers-panel">
      <div className="flex items-center justify-between p-3 border-b">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Schemalagda triggers
          </h3>
          <p className="text-xs text-muted-foreground">
            Cron eller interval — workflow körs automatiskt
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => void load()} disabled={isLoading}>
            <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="schedule-create-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />
            Schemalägg
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-2 space-y-2">
        {isLoading && triggers.length === 0 && (
          <div className="text-center py-6 text-xs text-muted-foreground">Laddar…</div>
        )}
        {!isLoading && triggers.length === 0 && (
          <div className="text-center py-6 text-xs text-muted-foreground">
            Inga scheman ännu.
          </div>
        )}
        {triggers.map((trigger) => {
          const disabled = trigger.disabled_at !== null;
          return (
            <div
              key={trigger.id}
              className={`p-2 rounded-md border ${disabled ? 'bg-muted/30 opacity-60' : 'bg-card'}`}
              data-testid={`schedule-row-${trigger.id}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-xs flex-1 truncate">{trigger.label}</span>
                <Badge variant="outline" className="text-[9px]">
                  {trigger.schedule_kind}
                </Badge>
                {disabled && <Badge variant="outline" className="text-[9px]">Disabled</Badge>}
                {!disabled && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Disable "{trigger.label}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Schemat stannar direkt — nästa fire-tid rensas.
                          Historik bevaras; du kan skapa en ny schedule när du vill.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Avbryt</AlertDialogCancel>
                        <AlertDialogAction onClick={() => void handleDisable(trigger.id, trigger.label)}>
                          Ja, disable
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground mb-1">
                {formatSchedule(trigger)}
              </div>
              <div className="grid grid-cols-2 gap-2 text-[10px] text-muted-foreground">
                <div>Triggers: {trigger.trigger_count}</div>
                <div>Senast: {formatDate(trigger.last_run_at)}</div>
                {!disabled && <div>Nästa: {formatDate(trigger.next_run_at)}</div>}
              </div>
            </div>
          );
        })}
      </div>

      <CreateScheduleDialog
        open={createOpen}
        workflowId={workflowId}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          void load();
        }}
      />
    </div>
  );
}

function CreateScheduleDialog({
  open, workflowId, onClose, onCreated,
}: {
  open: boolean;
  workflowId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState<ScheduleKind>('interval');
  const [intervalSeconds, setIntervalSeconds] = useState(3600);
  const [cronExpression, setCronExpression] = useState('0 0 9 * * *');
  const [timezone, setTimezone] = useState('UTC');
  const [submitting, setSubmitting] = useState(false);

  const cronValidation = kind === 'cron' ? validateCronExpression(cronExpression) : { ok: true as const };
  const canSubmit =
    label.trim().length > 0 &&
    (kind === 'interval' ? intervalSeconds >= 60 : cronValidation.ok);

  const preview = canSubmit
    ? computeNextRunAt(
        kind === 'interval'
          ? { kind: 'interval', intervalSeconds }
          : { kind: 'cron', cronExpression },
      )
    : null;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const result = await createScheduledTrigger({
      workflowId,
      label: label.trim(),
      kind,
      intervalSeconds: kind === 'interval' ? intervalSeconds : undefined,
      cronExpression: kind === 'cron' ? cronExpression : undefined,
      timezone,
    });
    setSubmitting(false);
    if (result.success) {
      toast({
        title: 'Schemalagt',
        description: `Nästa körning: ${result.next_run_at ? new Date(result.next_run_at).toLocaleString('sv-SE') : '—'}`,
      });
      setLabel('');
      onCreated();
    } else {
      toast({
        title: 'Kunde inte schemalägga',
        description: result.error === 'enterprise-required'
          ? 'Kräver Enterprise-plan'
          : result.error,
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Schemalägg workflow</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="schedule-label">Label</Label>
            <Input
              id="schedule-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="t.ex. 'Dagsrapport kl 09'"
              maxLength={100}
              data-testid="schedule-label-input"
            />
          </div>

          <Tabs value={kind} onValueChange={(v) => setKind(v as ScheduleKind)}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="interval">Interval</TabsTrigger>
              <TabsTrigger value="cron">Cron</TabsTrigger>
            </TabsList>

            <TabsContent value="interval" className="space-y-2 pt-2">
              <Label>Intervall</Label>
              <div className="grid grid-cols-2 gap-1">
                {INTERVAL_PRESETS.map((preset) => (
                  <Button
                    key={preset.seconds}
                    size="sm"
                    variant={intervalSeconds === preset.seconds ? 'default' : 'outline'}
                    onClick={() => setIntervalSeconds(preset.seconds)}
                    className="text-[11px] h-7"
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
              <Input
                type="number"
                value={intervalSeconds}
                onChange={(e) => setIntervalSeconds(Math.max(60, parseInt(e.target.value, 10) || 60))}
                min="60"
                className="mt-1"
                data-testid="schedule-interval-input"
              />
              <p className="text-[10px] text-muted-foreground">
                Minst 60 sekunder mellan körningar.
              </p>
            </TabsContent>

            <TabsContent value="cron" className="space-y-2 pt-2">
              <Label>Cron-expression (6 fält)</Label>
              <div className="grid grid-cols-1 gap-1">
                {CRON_PRESETS.map((preset) => (
                  <Button
                    key={preset.expression}
                    size="sm"
                    variant={cronExpression === preset.expression ? 'default' : 'outline'}
                    onClick={() => setCronExpression(preset.expression)}
                    className="text-[11px] h-7 justify-start"
                  >
                    <span className="font-mono mr-2">{preset.expression}</span>
                    <span className="text-muted-foreground">— {preset.label}</span>
                  </Button>
                ))}
              </div>
              <Input
                value={cronExpression}
                onChange={(e) => setCronExpression(e.target.value)}
                className="font-mono text-xs"
                placeholder="sekund minut timme dag månad veckodag"
                data-testid="schedule-cron-input"
              />
              {!cronValidation.ok && (
                <p className="text-[10px] text-destructive">
                  Ogiltig cron: {cronValidation.reason}
                </p>
              )}
              <div>
                <Label>Tidszon</Label>
                <Select value={timezone} onValueChange={setTimezone}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>
          </Tabs>

          {preview && (
            <div className="text-[11px] text-muted-foreground bg-muted/30 p-2 rounded-md" data-testid="schedule-preview">
              <strong>Preview:</strong> Nästa körning {preview.toLocaleString('sv-SE')}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Avbryt</Button>
            <Button
              onClick={() => void handleSubmit()}
              disabled={!canSubmit || submitting}
              data-testid="schedule-submit-btn"
            >
              {submitting ? 'Schemalägger…' : 'Schemalägg'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
